import { useI18n } from '../../i18n/useI18n';
import type { IdentityChoice } from '../../api/types';

/**
 * How a choice of identity provider reads, wherever it is shown.
 *
 * One component, so that a provider operator's read-only view of a tenancy and
 * the tenancy's own screen cannot drift into describing the same setting
 * differently.
 *
 * A choice of the provider's own carries no coordinates — it is a decision, and
 * the address belongs to the deployment. So the issuer to show arrives as a
 * prop, taken from the answer, and where the answer named none the summary says
 * so rather than putting an address of its own on screen.
 */
export function IdentityProviderSummary({
  choice,
  providerIssuer,
}: {
  choice: IdentityChoice | undefined;
  providerIssuer: string | undefined;
}) {
  const { t } = useI18n();

  if (choice === undefined) {
    return <p className="muted">{t('identityProvider.none')}</p>;
  }

  if (choice.kind === 'provider') {
    return (
      <dl className="dl">
        <dt>{t('identityProvider.field.kind')}</dt>
        <dd>{t('identityProvider.kind.provider')}</dd>
        <dt>{t('identityProvider.field.issuer')}</dt>
        <dd className="mono">{providerIssuer ?? t('identityProvider.issuer.unnamed')}</dd>
      </dl>
    );
  }

  return (
    <dl className="dl">
      <dt>{t('identityProvider.field.kind')}</dt>
      <dd>{t('identityProvider.kind.own')}</dd>
      <dt>{t('identityProvider.field.issuer')}</dt>
      <dd className="mono">{choice.issuer}</dd>
      <dt>{t('identityProvider.field.clientId')}</dt>
      <dd className="mono">{choice.client_id}</dd>
      <dt>{t('identityProvider.field.audience')}</dt>
      <dd className="mono">
        {choice.audience === '' ? t('identityProvider.audience.default') : choice.audience}
      </dd>
      <dt>{t('identityProvider.field.signingAlgs')}</dt>
      <dd className="mono">
        {choice.signing_algs.length === 0
          ? t('identityProvider.signingAlgs.all')
          : choice.signing_algs.join(' ')}
      </dd>
    </dl>
  );
}
